/**
 * Shared styles for navigation components
 *
 * These style utilities help reduce repetition across navigation items
 * by providing consistent theming and responsive behavior.
 */

/**
 * Base styles for navigation list items
 */
export const getBaseListItemStyles = (theme) => ({
  gap: .5,
  width: '100%',
  color: theme.palette.text.muted,
  height: 'var(--sidebar-item-height)',
  p: '0 0.5rem 0 0.375rem',
  borderRadius: 'var(--radius-md)',
});

/**
 * Styles for clickable navigation items with hover states
 */
export const getInteractiveListItemStyles = (theme, { miniDrawer, isDesktop } = {}) => ({
  ...getBaseListItemStyles(theme),
  cursor: 'pointer',
  border: 'var(--border-width) solid transparent',
  transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',

  ...(miniDrawer && isDesktop && {
    justifyContent: 'center',
    p: 0,
  }),

  '&:hover': {
    color: theme.palette.text.primary,
    bgcolor: 'var(--sidebar-item-hover-bg)',
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
  },
});

/**
 * Styles for navigation items with active state (NavLink)
 */
export const getNavLinkListItemStyles = (theme, { miniDrawer, isDesktop } = {}) => ({
  ...getInteractiveListItemStyles(theme, { miniDrawer, isDesktop }),

  '&.active': {
    color: theme.palette.text.primary,
    bgcolor: 'var(--sidebar-item-active-bg)',
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
  },
});

/**
 * Styles for list item icons
 */
export const getListItemIconStyles = ({ miniDrawer, isDesktop } = {}) => ({
  color: 'inherit',
  minWidth: 'unset',

  '& .MuiSvgIcon-root': {
    width: 20,
    height: 20,
  },

  ...(miniDrawer && isDesktop && {
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 0,
  }),
});

/**
 * Styles for list item text
 */
export const listItemTextStyles = {
  margin: 0,
};

/**
 * Typography props for list item text
 */
export const listItemTextTypographyProps = {
  fontWeight: 500,
  fontSize: 'var(--sidebar-item-font-size)',
};

/**
 * Determines if text should be shown based on drawer state
 */
export const shouldShowText = (isDesktop, miniDrawer) => {
  return !isDesktop || !miniDrawer;
};

/**
 * Styles for logout button and similar action items
 */
export const getActionListItemStyles = (theme) => ({
  ...getBaseListItemStyles(theme),
  cursor: 'pointer',
  justifyContent: 'center',
  border: 'var(--border-width) solid transparent',

  '&:hover': {
    color: theme.palette.text.primary,
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
  },
});
